<template>
	<div class="contentBlock">
		<fragment v-if="isStaff">
			<div class="sectionTitle">Consigned Property List</div>
			<div
				v-for="(list, listIndex) in sortedInventoryLists"
				:id="listIsSentOrSigned(list) ? 'inventory_list' : 'unsigned_inventory_list'"
				:key="list.id || list.tempId"
				:class="listIsSentOrSigned(list) ? 'contentBlock' : 'contentBlock'"
			>
				<div
					v-for="(item, index) in list.items"
					:id="'inventory_item_parent_' + listIndex + '_' + (index + 1)"
					:key="item.id || item.tempId"
					class="inventory_item_parent"
					:data-list-id="list.id"
				>
					<div
						:id="'inventory_item_' + listIndex + '_' + (index + 1) + '_left'"
						class="contentColumnWide paddingRight"
					>
						<span
							v-if="isListDisabled() || item.inventory_ids.length > 0"
							class="value contentBlock paddingLeft"
						>
							{{ item.description }}
						</span>
						<input
							v-else
							:id="'inventory_list_' + listIndex + '_item_' + (index + 1) + '_description'"
							v-model="item.description"
							class="inventory_list_item_description contentBlock paddingLeft"
							:name="'inventory_list_item_description_' + listIndex + '_' + (index + 1)"
							type="text"
							maxlength="250"
							placeholder="Description"
							@input="checkInventoryListInputsForValues(list)"
							@keyup="($event) => handleDescriptionChange($event, item, list, index)"
						/>
					</div>
					<div
						:id="'inventory_item_' + listIndex + '_' + (index + 1) + '_right'"
						class="contentColumnThin hideOnPrint"
					>
						<div
							v-if="isListDisabled() || item.inventory_ids.length > 0"
							class="value contentBlock textCenter paddingRightMore"
						>
							{{ item.lotcount }}
						</div>
						<input
							v-else
							:id="'inventory_list_' + listIndex + '_item_' + (index + 1) + '_amount'"
							v-model="item.lotcount"
							class="inventory_list_item_lot_count textCenter"
							:name="'inventory_list_item_lot_count_' + listIndex + '_' + (index + 1)"
							type="number"
							min="1"
							placeholder=" Approx. Lot Count"
							@input="checkInventoryListInputsForValues(list)"
							@change="($event) => handleDescriptionChange($event, item, list, index)"
						/>
						<div
							v-if="isListSignedByCustomer(list)"
							class="addInventoryButton hideOnPrint"
						>
							<span class="marginLeft">
								<a
									v-if="item.id"
									target="_blank"
									title="Add Inventory"
									:href="`/lladmin/?inventory_list_item_id=${item.id}&submit=Go&create_inventory_with_item_id=y&menuitem=addinventory`"
								>
									<small>+ Add Inventory ({{ item.inventory_ids.length }})</small>
								</a>
							</span>
						</div>
					</div>
				</div>
			</div>
		</fragment>

		<!-- Customer View (Non-Staff) -->
		<fragment v-else-if="hasInventoryItems">
			<div class="sectionTitle">Consigned Property List</div>
			<div
				v-for="(list, listIndex) in sortedInventoryLists"
				:key="list.id || list.tempId"
				class="contentBlock"
			>
				<div
					v-for="(item, index) in list.items"
					:id="'inventory_item_parent_' + listIndex + '_' + (index + 1)"
					:key="item.id || item.tempId"
					class="contentBlock marginBottomSmaller"
				>
					<div
						:id="'inventory_item_' + listIndex + '_' + (index + 1) + '_left'"
						class="contentBlock"
					>
						<span class="value contentBlock paddingLeft">
							{{ item.description }}
						</span>
					</div>
				</div>
				<!-- Show if no items -->
				<div
					v-if="!list.items.length"
					class="contentBlock marginBottomSmaller"
				>
					<div class="contentColumnLeftIgnoreMobile">
						<div class="disabledValue textCenter">No items listed</div>
					</div>
				</div>
			</div>
		</fragment>
	</div>
</template>

<script>
	import { Fragment } from 'vue-frag'
	import contractState from '@/mixins/contractState'
	import { mapMutations } from 'vuex'

	export default {
		name: 'InventoryList',
		components: {
			Fragment
		},
		mixins: [contractState],
		props: {
			contract: {
				type: Object,
				required: true
			}
		},
		data() {
			return {
				lastBlankLineId: null
			}
		},
		computed: {
			/**
			 * Whether the contract has inventory items
			 * @returns {boolean} True if the contract has inventory items
			 */
			hasInventoryItems() {
				// True if any inventory_list has an id and items greater than 0.
				return this.contract.inventory_lists.some((list) => list.id && list.items?.length > 0)
			},

			/**
			 * Gets the inventory lists sorted by ID in ascending order
			 * @returns {Array} Sorted inventory lists
			 */
			sortedInventoryLists() {
				if (!this.contract.inventory_lists?.length) return []
				const filteredLists = [...this.contract.inventory_lists].sort((a, b) => {
					// Handle null IDs (new lists) by putting them at the end
					if (a.id === null) return 1
					if (b.id === null) return -1
					return a.id - b.id
				})
				return filteredLists
			},
			/**
			 * Whether all inventory lists are signed by staff
			 * @returns {boolean} True if all lists are signed by staff
			 */
			allListsSignedByStaff() {
				if (!this.contract.inventory_lists?.length) return false
				return this.contract.inventory_lists.every((list) => this.listIsSignedByStaff(list))
			},

			/**
			 * Whether all inventory lists are signed by customer
			 * @returns {boolean} True if all lists are signed by customer
			 */
			allListsSignedByCustomer() {
				if (!this.contract.inventory_lists?.length) return false
				return this.contract.inventory_lists.every((list) => this.listIsSignedByCustomer(list))
			},
			/**
			 * Whether a new list should be started
			 * @returns {boolean} True if a new list should be started
			 */
			shouldStartNewList() {
				if (!this.isEditable(true)) return false
				if (!this.contract.inventory_lists?.length) return true

				// Filter out lists with null IDs and filtered items to 0 (also with no description or lot count)
				const lastList = this.contract.inventory_lists[this.contract.inventory_lists.length - 1]
				return this.listIsSignedByCustomer(lastList) && this.listIsSignedByStaff(lastList)
			}
		},
		watch: {
			/**
			 * Watch for changes in the contract
			 * @param {Object} newVal - The new contract value
			 */
			contract: {
				handler(newVal) {
					// Initialize inventory_lists if it doesn't exist
					if (!newVal.inventory_lists) {
						this.setContractData({
							...newVal,
							inventory_lists: []
						})
						return
					}

					// Add new list if needed
					if (this.isStaff && this.shouldStartNewList) {
						this.addNewList()
						return
					}

					// Only add empty item to the last unsigned list if we're staff
					if (this.isStaff) {
						const lastList = newVal.inventory_lists[newVal.inventory_lists.length - 1]
						if (lastList && !this.isListDisabled() && this.shouldAddNewRow(lastList)) {
							this.addEmptyItem(lastList)
						}
					}
				},
				deep: true
			}
		},
		created() {
			// Initialize inventory lists if needed
			if (!this.contract.inventory_lists) {
				this.setContractData({
					...this.contract,
					inventory_lists: []
				})
			}
			// Add first list if none exist and we're staff
			if (this.isStaff && this.shouldStartNewList) {
				this.addNewList()
			}
			// Add empty item to the last unsigned list if we're staff
			if (this.isStaff) {
				const lastList = this.contract.inventory_lists[this.contract.inventory_lists.length - 1]
				if (lastList && !this.isListDisabled() && this.shouldAddNewRow(lastList)) {
					this.addEmptyItem(lastList)
				}
			}
		},
		methods: {
			...mapMutations({
				setContractData: 'SET_CONTRACT_DATA'
			}),
			/**
			 * Whether the list is disabled
			 * @returns {boolean} True if the list is disabled, false otherwise
			 */
			isListDisabled() {
				return !this.isStaff || !this.isEditable(true)
			},
			/**
			 * Whether the list is sent or signed by the customer
			 * @param {Object} list - The list to check
			 * @returns {boolean} True if the list is sent or signed by the customer, false otherwise
			 */
			isListSignedByCustomer(list) {
				return list.customer_signature && list.customer_signature_date
			},
			/**
			 * Add a new empty inventory list
			 */
			addNewList() {
				const updatedContract = { ...this.contract }
				const newList = {
					id: null,
					items: [],
					signed_by_name: null,
					signed_date: null,
					customer_signature: null,
					customer_signature_date: null,
					sent_customer_email_unixtime: null,
					tempId: null
				}
				newList.tempId = 'new_list_' + Math.random().toString(36).substring(2, 15)

				// When we add a new list, and our previous list has a signed_by_name, we should copy it to our new list
				if (this.contract.inventory_lists.length > 0) {
					const previousList =
						this.contract.inventory_lists[this.contract.inventory_lists.length - 1]
					if (previousList.signed_by_name) {
						newList.signed_by_name = previousList.signed_by_name
						newList.signed_date = previousList.signed_date
					}
				}

				if (!updatedContract.inventory_lists) {
					updatedContract.inventory_lists = []
				}

				updatedContract.inventory_lists.push(newList)
				this.setContractData(updatedContract)
				this.addEmptyItem(newList)
			},
			/**
			 * Add an empty item to the specified inventory list
			 * @param {Object} list - The list to add an empty item to
			 */
			async addEmptyItem(list) {
				// Don't add empty items to lists that are signed by the customer
				if (this.isListSignedByCustomer(list)) {
					return
				}

				await this.$nextTick()

				// Store the currently focused element before adding a new item
				const activeElement = document.activeElement
				const activeElementId = activeElement ? activeElement.id : null

				if (!list.items) {
					list.items = []
				}

				// Generate a unique tempId for the blank line
				const tempId = 'blank_' + Math.random().toString(36).substring(2, 15)
				this.lastBlankLineId = tempId

				const newItem = {
					id: null,
					description: '',
					lotcount: '',
					inventory_ids: [],
					tempId: tempId // Add tempId to identify blank lines
				}

				// Check if we already have a blank line
				const hasEmptyItem = list.items.some((item) => !item.description && !item.lotcount)

				if (!hasEmptyItem) {
					list.items.push(newItem)
					this.setContractData({ ...this.contract })

					// Restore focus to the previously active element after the DOM updates
					this.$nextTick(() => {
						if (activeElementId && document.getElementById(activeElementId)) {
							document.getElementById(activeElementId).focus()
						}
					})
				}
			},
			/**
			 * Whether to add a new row to the inventory list
			 * @param {Object} list - The list to check
			 * @returns {boolean} True if a new row should be added, false otherwise
			 */
			shouldAddNewRow(list) {
				// Don't add new rows to signed lists
				if (this.isListSignedByCustomer(list)) {
					return false
				}

				return (
					list.items?.every(
						(item) => item.description?.trim() && item.lotcount?.toString().trim()
					) || false
				)
			},
			/**
			 * Check the inventory list inputs for values
			 * @param {Object} list - The list to check
			 */
			checkInventoryListInputsForValues(list) {
				// Only add new row if needed and list is not signed
				if (!this.isListSignedByCustomer(list) && this.shouldAddNewRow(list)) {
					this.addEmptyItem(list)
				}
				// Emit updated inventory lists to parent
				this.$emit('update:inventoryLists', this.contract.inventory_lists)
			},
			/**
			 * Whether the list is sent or signed by the customer
			 * @param {Object} list - The list to check
			 * @returns {boolean} True if the list is sent or signed by the customer, false otherwise
			 */
			listIsSentOrSigned(list) {
				return list.sent_customer_email_unixtime || list.customer_signature
			},
			/**
			 * Whether the list is signed by the staff
			 * @param {Object} list - The list to check
			 * @returns {boolean} True if the list is signed by the staff, false otherwise
			 */
			listIsSignedByStaff(list) {
				return list.signed_by_name && list.signed_date
			},
			/**
			 * Whether the list is signed by the customer
			 * @param {Object} list - The list to check
			 * @returns {boolean} True if the list is signed by the customer, false otherwise
			 */
			listIsSignedByCustomer(list) {
				return list.customer_signature && list.customer_signature_date
			},
			/**
			 * Handles changes to the description field
			 * Removes items with empty description and lotcount except for the last row
			 * @param {Object} item - The changed inventory item
			 * @param {Object} list - The parent inventory list
			 * @param {Number} index - The item's index in the list
			 */
			handleDescriptionChange(event, item, list) {
				// Skip if the list is disabled
				if (this.isListDisabled()) {
					return
				}

				// Store the currently focused element
				const activeElement = document.activeElement
				const activeElementId = activeElement ? activeElement.id : null
				const selectionStart =
					activeElement && activeElement.selectionStart ? activeElement.selectionStart : null
				const selectionEnd =
					activeElement && activeElement.selectionEnd ? activeElement.selectionEnd : null

				// If item now has content, remove its tempId
				if ((item.description?.trim() || item.lotcount?.toString().trim()) && item.tempId) {
					delete item.tempId
				}

				// Keep the blank line with the lastBlankLineId (our designated blank line)
				// and remove all other blank lines
				list.items = list.items.filter((listItem) => {
					// If item has a database ID but is now blank, remove it
					if (
						listItem.id &&
						!listItem.description?.trim() &&
						!listItem.lotcount?.toString().trim()
					) {
						return false
					}

					// Keep items with content
					if (listItem.description?.trim() || listItem.lotcount?.toString().trim()) {
						return true
					}

					// Keep our designated blank line (only if list is not signed)
					return !this.isListSignedByCustomer(list) && listItem.tempId === this.lastBlankLineId
				})

				// If the list now has 0 items, remove it
				if (list.items.length === 0) {
					this.removeList(list)
				}

				// Ensure there's always an empty row at the bottom with a tempId (only for unsigned lists)
				if (!this.isListSignedByCustomer(list)) {
					const lastItem = list.items[list.items.length - 1]
					if (lastItem && (lastItem.description?.trim() || lastItem.lotcount?.toString().trim())) {
						// If last item has content, add a new blank item to the last list
						const lastList = this.contract.inventory_lists[this.contract.inventory_lists.length - 1]
						this.addEmptyItem(lastList)
					} else if (lastItem && !lastItem.tempId) {
						// If last item is blank but doesn't have a tempId, add one
						const tempId = 'blank_' + Math.random().toString(36).substring(2, 15)
						lastItem.tempId = tempId
						this.lastBlankLineId = tempId
					}
				}

				// Emit updated inventory lists to parent
				this.$emit('update:inventoryLists', this.contract.inventory_lists)

				// Restore focus to the previously active element after the DOM updates
				this.$nextTick(() => {
					if (activeElementId && document.getElementById(activeElementId)) {
						const elementToFocus = document.getElementById(activeElementId)
						elementToFocus.focus()

						// Restore cursor position if applicable
						if (selectionStart !== null && selectionEnd !== null) {
							elementToFocus.setSelectionRange(selectionStart, selectionEnd)
						}
					}
				})
			},
			/**
			 * Remove a list from the contract
			 * @param {Object} list - The list to remove
			 */
			removeList(list) {
				this.setContractData({
					...this.contract,
					inventory_lists: this.contract.inventory_lists.filter((l) => l !== list)
				})
			}
		}
	}
</script>

<style scoped>
	.inventory_item_parent {
		display: flex;
		justify-content: space-between;
		flex-direction: row;
	}

	.list-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	.list-actions {
		display: flex;
		gap: 1rem;
	}

	.addInventoryButton {
		position: absolute;
		left: 100%;
		top: 6px;
		width: max-content;
		display: none;
	}

	.addInventoryButton a {
		text-decoration: none;
	}

	input[type='number']::-webkit-inner-spin-button,
	input[type='number']::-webkit-outer-spin-button {
		opacity: 1;
	}

	/* Only show addInventoryButton on screens that are not mobile */
	@media (min-width: 768px) {
		.addInventoryButton {
			display: block;
		}
	}
</style>
